<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="trophy"></font-awesome-icon>
          {{ $t('pointTransactionHistory') }}
        </h1>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <form-input class="mr-1 mb-2" :model="filterFormModel" attribute="keyword" :placeholder="$t('enterKeyword')"
                        v-on:keydown.enter="load"></form-input>
            <client-select v-if="!clientId" class="mr-1 mb-2" :model="filterFormModel"
                           attribute="client" :isFilter="true" :place-holder="$t('selectClient')"></client-select>
            <form-select :model="filterFormModel" attribute="action" class="mr-1 mb-2"
                         :options="[
                             {value: null, text: $t('selectAction') },
                             {value: 'plus', text: $t('plus') },
                             {value: 'minus', text: $t('minus') },
                         ]"></form-select>
            <form-select :model="filterFormModel" attribute="type" class="mr-1 mb-2"
                         :options="[
                {value: null, text: $t('selectType') },
                {value: 'order', text: $t('order') },
                {value: 'manual', text: $t('manual') },
                {value: 'payment', text: $t('payment') },
            ]"></form-select>
            <time-range-picker :model="filterFormModel" :label="$t('time')" class="mr-1 mb-2"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-2">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-2">Reset</button>
            <point-transaction-export
                v-if="['owner', 'admin', 'tech', 'operationsManager', 'account'].includes($store.state.user.role)"
                :filter="filterFormModel" :total-count="itemCount"/>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount > 0">
            {{ $t('found', [itemCount]) }}
          </div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount"
                            :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="items.length > 0">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-2">{{ $t('client') }}</div>
            <div class="col-lg-1">{{ $t('createTime') }}</div>
            <div class="col-lg-3">{{ $t('info') }}</div>
            <div class="col-lg-1">{{ $t('point') }}</div>
            <div class="col-lg-1">{{ $t('afterPoint') }}</div>
            <div class="col-lg-3">{{ $t('content') }}</div>
            <div class="col-lg-1"></div>
          </div>
          <div class="row" v-for="item in items" :key="item._id">
            <div class="col-lg-2">
              <span><b>{{ $t('name') }}: </b> {{ item.client.companyName }}</span>
              <span><b>Domain: </b>{{ item.client.domain }}</span>
            </div>
            <div class="col-lg-1">
              <p>{{ item.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</p>
            </div>
            <div class="col-lg-3">
              <div v-if="item.type === 'order'">
                <span class="badge badge-success">{{ $t(item.type) }}</span>
                <div v-if="item.order">{{ $t('order') }}: {{ item.order.code }}</div>
                <div v-if="item.customer">{{ $t('customer') }}: {{ item.customer.name }}</div>
              </div>
              <div v-if="item.type === 'manual'">
                <span class="badge badge-info">{{ $t(item.type) }}</span>
                <div v-if="item.customer">{{ $t('customer') }}: {{ item.customer.name }}</div>
              </div>
              <div v-if="item.type === 'payment'">
                <span class="badge badge-primary">{{ $t(item.type) }}</span>
                <div v-if="item.order">{{ $t('order') }}: {{ item.order.code }}</div>
                <div v-if="item.customer">{{ $t('customer') }}: {{ item.customer.name }}</div>
              </div>
            </div>
            <div class="col-lg-1">
              <p>{{ formatPoint(item) }}</p>
            </div>
            <div class="col-lg-1">
              <p>{{ getTotalPoint(item) }}</p>
            </div>
            <div class="col-lg-3">
              <p>{{ item.content }}</p>
            </div>
            <div class="col-lg-1">
              <div class="c-smart-table__content">
                <div class="c-buttons clearfix is-2">
                  <div class="c-buttons__col">
                    <button @click="del(item._id)" type="button" class="btn btn-sm btn-danger mr-1">
                      <font-awesome-icon icon="trash"/>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-danger" v-if="items.length === 0 && !loading">
        {{ $t('notFound') }}
      </div>
      <div class="col-12" v-if="itemCount > 0">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">
            {{ $t('found', [itemCount]) }}
          </div>
          <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                        v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                        @input="load">
          </b-pagination>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import List from "@/mixins/List";
import CRUD from "@/mixins/CRUD";
import TimeRangePicker from "@/components/TimeRangePicker.vue";
import ClientSelect from "@/components/ClientSelect.vue";
import PointTransactionExport from "@/views/message/export/PointTransactionExport.vue";

export default {
  components: {PointTransactionExport, ClientSelect, TimeRangePicker},
  mixins: [List, CRUD],
  props: {
    clientId: {
      type: String,
      default: null
    }
  },
  data: function () {
    return {
      service: '/client-point-transaction',
      autoLoad: true,
      defaultFilterFormModel: {
        client: this.clientId || null,
        keyword: null,
        action: null,
        type: null,
      }
    }
  },
  methods: {
    formatPoint(item) {
      const point = Math.abs(item.point);
      return item.action === 'plus' ? '+' + point : '-' + point;
    },
    getTotalPoint(item) {
      const point = Math.abs(item.point);
      return item.action === 'plus'
          ? item.beforePoint + point
          : item.beforePoint - point;
    }
  },
  i18n: {
    messages: {
      en: {
        pointTransactionHistory: "Point Transaction History",
        createTime: "Create time",
        info: "Info",
        point: "Point",
        afterPoint: "After point",
        content: "Content",
        order: "Order",
        customer: "Customer",
        plus: "Plus",
        minus: "Minus",
        enterKeyword: "Enter keyword",
        selectType: "Select type",
        manual: "Manual",
        selectAction: "Select action",
        payment: "Payment",
        notFound: "Not found any record",
      },
      vi: {
        pointTransactionHistory: "Lịch sử tích điểm",
        createTime: "Tạo lúc",
        info: "Thông tin",
        point: "Điểm",
        afterPoint: "Điểm sau giao dịch",
        content: "Nội dung",
        order: "Đơn hàng",
        customer: "Khách hàng",
        plus: "Tăng",
        minus: "Giảm",
        enterKeyword: "Nhập từ khóa",
        selectType: "Chọn loại",
        manual: "Thủ công",
        selectAction: "Chọn hành động",
        payment: "Thanh toán",
        notFound: "Không tìm thấy bản ghi nào",
      }
    }
  }
}
</script>